import React, { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import TextButton from 'components/Luxkit/Button/TextButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextInput from 'components/Common/Form/Input/TextInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import Select from 'components/Common/Form/Input/Select'
import moment from 'moment'
import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import { getData as getCountries } from 'country-list'
import { formatTourV2VariationDetailCounts } from 'lib/tours/extractTourV2VariationDetails'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { formToObject } from 'lib/forms/formToObject'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { sendPrivateRequestForTourV2 } from 'api/tourV2'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import { getOfferPackageUpgradeDisplayTextMap, PACKAGE_UPGRADE_VALUES } from 'constants/tours'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import Group from 'components/utils/Group'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'

const FormFieldsContainer = styled.div`
  margin-top: ${rem(16)};
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-column-gap: ${rem(12)};
    grid-template-columns: 1fr 1fr;

    .full-width-column {
      grid-column: span 2;
    }
  }
`

const AlertContainer = styled(Group)`
  padding: ${rem(4)};
  margin: ${rem(16)} 0;
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const OccupancyContainer = styled.div`
  margin-top: ${rem(4)};
`

const ROOM_CAPACITIES: Array<App.PackageRoomCapacity> = [{
  adults: 20,
  children: 10,
  infants: 0,
}]

interface MappedStateProps {
  firstName?: string;
  lastName?: string;
  phone?: string;
  phonePrefix?: string;
  email?: string;
  countryCode: string;
  customerId?: string;
}
interface Props {
  variations: Record<string, Tours.TourV2OfferVariation>;
  tourId: string;
  departureDate?: string;
}

interface VariationOption {
  id: string;
  value: string;
  price: number | null;
  slug: string;
}

function PrivateRequestFormModal(props: Props & MappedStateProps) {
  const {
    firstName,
    lastName,
    phone,
    phonePrefix,
    countryCode,
    email,
    variations,
    tourId,
    departureDate,
    customerId,
  } = props

  const dispatch = useAppDispatch()

  const startDateToBook = moment().add('3', 'weeks')
  const [selectedVariation, setSelectedVariation] = useState<VariationOption>()
  const variationOptions = useMemo<Array<VariationOption>>(() => {
    return Object.values(variations)
      .map((variation) => ({
        id: variation.fkTourOptionId,
        value: `${variation.name} - ${formatTourV2VariationDetailCounts(variation).variationDetailsCountText}`,
        price: variation.privateRequestFromPrice,
        slug: variation.id,
      }))
  }, [variations])

  const packageUpgradesOptions = useMemo<Array<{ value: string, label: string}>>(() => {
    const defaultVariationId = selectedVariation?.slug ?? Object.keys(variations)[0]
    return variations[defaultVariationId]?.availablePackageUpgrades?.map((packageOption) => ({
      value: packageOption.packageOption,
      label: getOfferPackageUpgradeDisplayTextMap(tourId).get(packageOption.packageOption.toLowerCase() as PACKAGE_UPGRADE_VALUES),
    })) ?? []
  }, [selectedVariation?.slug, tourId, variations])

  const maxChildAge = useMemo(() => Object.values(variations)[0].maxChildPriceAge, [variations])
  const isLoggedIn = useMemo(() => !!customerId, [customerId])
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>([{
    adults: 2,
    children: 0,
  }])
  const handleOccupancies = useCallback((occupancies: Array<App.Occupants>) => setOccupancies(occupancies), [setOccupancies])

  const [fromPrice, setFromPrice] = useState(Object.values(variations)[0].privateRequestFromPrice)
  const handleVariationChange = useCallback((e) => {
    const variationSelected = variationOptions.find(variationOption => variationOption.id === e.target.value)
    setSelectedVariation(variationSelected)
    setFromPrice(variationSelected.price)
  }, [variationOptions])

  const modalState = useModalElementContext<unknown>()

  const sendRequest = useCallback(async(e) => {
    const requestPrivateTourWithoutTourOptionId:Omit<Tours.RequestPrivateTour, 'tourOptionId'> = formToObject(e.currentTarget)

    const variationOption = variationOptions.find(variationOption => variationOption.id === requestPrivateTourWithoutTourOptionId.variation)

    const fullPayload: Tours.RequestPrivateTour = {
      ...requestPrivateTourWithoutTourOptionId,
      variation: variationOption.value,
      tourOptionId: variationOption.id,
      occupancy: occupancies.map(occupant => ({
        adults: occupant.adults,
        children: occupant.children,
        infants: occupant.infants,
        childrenAge: occupant.childrenAge,
      })),
    }

    const response = await sendPrivateRequestForTourV2(fullPayload)

    modalState.resolve(response)
  }, [modalState, variationOptions, occupancies])

  const tourUrl = useMemo(() => {
    if (selectedVariation) {
      const urls = window.location.href.split('/')
      urls.splice(-1, 1, selectedVariation.slug)
      return urls.join('/')
    }
    return window.location.href
  }, [selectedVariation])

  const sendRequestIfLoggedIn = useCallback<FormEventHandler<HTMLFormElement>>(async(e) => {
    e.preventDefault()

    if (!isLoggedIn) {
      dispatch(accountAccessShowLogin())
      modalState.resolve(undefined)
      return
    }

    await sendRequest(e)
  }, [isLoggedIn, dispatch, sendRequest, modalState])

  return <ModalBase size="S">
    <form onSubmit={sendRequestIfLoggedIn}>
      <ModalHeader title="Request a private tour" />
      <ModalBody>
        <ModalContent>
          <BodyText variant="large">
            Please let us know your details and our Tours Concierge will start tailoring your private tour.
          </BodyText>
          <FormFieldsContainer>
            <HiddenInput name="tourId" value={tourId} />
            {customerId && <HiddenInput name="customerId" value={customerId} />}
            <HiddenInput name="tourUrl" value={tourUrl} />
            <TextInput
              label="First name"
              name="firstName"
              required
              placeholder="First name"
              defaultValue={firstName}
            />
            <TextInput
              label="Last name"
              name="lastName"
              required
              placeholder="Last name"
              defaultValue={lastName}
            />
            <TextInput
              className="full-width-column"
              type="email"
              label="Email address"
              name="email"
              required
              defaultValue={email}
            />
            <PhoneInput
              label="Mobile number"
              name="phone"
              id="mobileNumber"
              required
              defaultPhoneNumber={phone}
              defaultPhonePrefix={phonePrefix}
              prefixCountry={countryCode}
            />
            <Select
              label="Your home country"
              name="countryCode"
              defaultValue={countryCode}
              required
              noValidationSpacing
            >
              {getCountries().map(country =>
                <option key={country.code} value={country.code}>{country.name}</option>,
              )}
            </Select>
            {variationOptions.length === 1 && <HiddenInput name="variation" value={variationOptions[0].id} />}
            <Select
              className="full-width-column"
              label="Which tour are you interested in?"
              name="variation"
              defaultValue={variationOptions[0].id}
              onChange={handleVariationChange}
              required
              disabled={variationOptions.length === 1}
            >
              {variationOptions.map(variationOption =>
                <option key={variationOption.id} value={variationOption.id}>{variationOption.value}</option>,
              )}
            </Select>
            {packageUpgradesOptions.length === 1 && <HiddenInput name="packageOption" value={packageUpgradesOptions[0].value} />}
            {packageUpgradesOptions.length > 1 && <Select
              className="full-width-column"
              label="Available upgrades"
              name="packageOption"
              defaultValue={packageUpgradesOptions[0].value}
              required
            >
              {packageUpgradesOptions.map(packageOption =>
                <option key={packageOption.value} value={packageOption.value}>{packageOption.label}</option>,
              )}
            </Select>}
            <DatePickerInput
              className="full-width-column"
              label="When are you looking to travel?"
              name="planningTravelDate"
              min={startDateToBook.toDate()}
              defaultMonth={startDateToBook.month()}
              getInvalidMessage={undefined}
              displayFormat="DD/MM/YYYY"
              placeholder="Select your date"
              defaultValue={departureDate}
              endIcon={<LineCalendarIcon />}
              required
            />
            <BodyText className="full-width-column" weight="bold" variant="medium">Who are you travelling with?</BodyText>
            <OccupancyContainer className="full-width-column">
              <OccupancyPicker
                compact
                name="occupancy"
                maxRooms={10}
                capacities={ROOM_CAPACITIES}
                occupancies={occupancies}
                childrenAgeLabel="at time of departure"
                maxChildAge={maxChildAge}
                onChange={handleOccupancies}
                enableMultiRoom
              />
            </OccupancyContainer>
            <AlertContainer direction="horizontal" horizontalAlign="space-evenly" className="full-width-column">
              <BodyTextBlock variant="small" startIcon={<LineInfoCircleIcon />}>Pricing subject to seasonal availability and will be confirmed by your Tour Concierge.</BodyTextBlock>
            </AlertContainer>
            <TextArea
              className="full-width-column"
              name="comment"
              noValidationSpacing
              resize="vertical"
              label="Any other questions or comments?"
              placeholder="eg. Do I need a vaccine? I wish to travel with children ..."
              maxLength={150}
            />
          </FormFieldsContainer>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          {fromPrice && <div>
            <PriceRowPriceCaption>From</PriceRowPriceCaption>
            <PriceRowPrice
              size="M"
              price={fromPrice}
              saleUnit="person"
            />
            <PriceRowTaxesAndFees />
            <PriceRowCaption>Twin Share</PriceRowCaption>
          </div>}
          <TextButton type="submit" kind="primary">Submit Request</TextButton>
        </Group>
      </ModalFooter>
    </form>
  </ModalBase>
}

const mapStateToProps = (state: App.State): MappedStateProps => {
  return {
    firstName: state.auth.account.givenName,
    lastName: state.auth.account.surname,
    phone: state.auth.account.phone,
    phonePrefix: state.auth.account.phonePrefix,
    email: state.auth.account.email,
    countryCode: state.geo.currentRegionCode,
    customerId: state.auth.account.memberId,
  }
}

export default connect(mapStateToProps)(PrivateRequestFormModal)
